import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { useLanguage } from '../../i18n/LanguageContext';

// Copy is owned by the app. Server diagnostics and supplied message bodies are never rendered.
export const operationalNoticeMessages = {
  order_shortage: 'An order is waiting for product units. Review the order and tissue bank.',
  order_review: 'An order needs distributor review before processing can continue.',
  purchase_uncertain: 'A product-unit payment needs verification. Do not submit it again.',
  purchase_return:
    'A product-unit payment needs recovery review. Review the affected product family.',
};
export const operationalNoticeTitles = {
  order_shortage: 'Order awaiting units',
  order_review: 'Order review required',
  purchase_uncertain: 'Payment result unconfirmed',
  purchase_return: 'Payment return or dispute',
};
const idPattern = /^[a-f\d]{24}$/i;
const date = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const allowed = (admin) => {
  try {
    return (
      JSON.parse(localStorage.getItem('user'))?.role === (admin ? 'admin' : 'master_distributor')
    );
  } catch {
    return false;
  }
};

export default function DistributorOperationalNotices({
  admin = false,
  compact = false,
  refresh = 0,
}) {
  const { t, locale } = useLanguage();
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const { begin, version } = useShippingRequest(`operational-notices:${admin}`);
  const load = useCallback(
    async (nextPage = 1) => {
      const request = begin();
      setQueue(null);
      setError('');
      setLoading(true);
      if (!allowed(admin)) {
        setLoading(false);
        return;
      }
      try {
        const value = (
          await distributorService.operationalNotices(admin, nextPage, { signal: request.signal })
        )?.data?.data;
        if (!request.isCurrent()) return;
        if (
          !Array.isArray(value?.items) ||
          value.items.length > 20 ||
          value.page !== Math.min(nextPage, Math.max(1, value.totalPages)) ||
          !Number.isSafeInteger(value.total) ||
          value.total < 0 ||
          value.totalPages !== Math.ceil(value.total / 20) ||
          typeof value.emailEnabled !== 'boolean' ||
          typeof value.discoveryEnabled !== 'boolean' ||
          new Set(value.items.map((row) => row?._id)).size !== value.items.length ||
          value.items.some(
            (row) =>
              !idPattern.test(row?._id) ||
              !idPattern.test(row.sourceId) ||
              !Object.hasOwn(operationalNoticeMessages, row.kind) ||
              !date(row.createdAt) ||
              typeof row.active !== 'boolean' ||
              !(
                row.sourceLabel == null ||
                (row.kind.startsWith('order_') &&
                  typeof row.sourceLabel === 'string' &&
                  row.sourceLabel.length <= 30 &&
                  /^ORD-\d{4}-\d{4,}$/.test(row.sourceLabel))
              ) ||
              !(row.resolvedAt == null || date(row.resolvedAt)) ||
              (admin &&
                (typeof row.distributorId !== 'string' || !idPattern.test(row.distributorId)))
          )
        )
          throw new Error('Invalid operational notices');
        setQueue(value);
        setPage(value.page);
      } catch {
        if (request.isCurrent()) setError('Operational notices are unavailable. Please retry.');
      } finally {
        if (request.isCurrent()) setLoading(false);
      }
    },
    [admin, begin]
  );
  useEffect(() => {
    load(1);
  }, [load, version, refresh]);
  if (!allowed(admin)) return null;
  return (
    <section
      className={
        compact
          ? 'me-overview-panel me-overview-notices'
          : admin
            ? 'me-distributor-admin-card space-y-4'
            : 'me-dw-card mt-5 p-5 space-y-4'
      }
      aria-label={t('Operational notices')}
    >
      <div
        className={
          compact ? 'me-overview-panel-heading' : 'flex items-center justify-between gap-3'
        }
      >
        <h2 className={compact ? undefined : 'text-lg font-semibold'}>
          {t(compact ? 'Updates' : 'Operational notices')}
        </h2>
        {!compact && (
          <button type="button" disabled={loading} onClick={() => load(page)}>
            {t('Refresh notices')}
          </button>
        )}
      </div>
      {!admin && !compact && (
        <p className="text-sm text-slate-600">
          {t(
            'Private order and payment updates for distributor users and MedEffects staff. Separate from low-bank alerts.'
          )}
        </p>
      )}
      {loading && <p role="status">{t('Loading operational notices...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {queue && !compact && (
        <p className="text-sm text-slate-600">
          {t(
            queue.emailEnabled
              ? 'Operational notice emails are enabled.'
              : 'Operational notice emails are off. In-app notices remain available.'
          )}
        </p>
      )}
      {queue && !queue.discoveryEnabled && (
        <p role="status">
          {t(
            compact
              ? 'Automatic updates are paused.'
              : 'Automatic notice checks are off. Existing notices remain available.'
          )}
        </p>
      )}
      {queue?.items.length === 0 && (
        <p>{t(compact ? 'No notices to show.' : 'No operational notices yet.')}</p>
      )}
      {queue?.items.map((row) => (
        <article key={row._id} className="border-t border-slate-200 pt-3 space-y-1">
          <h3 className="font-semibold">
            {t(operationalNoticeTitles[row.kind])} ·{' '}
            {t(row.active ? 'Needs attention' : 'Notice resolved')}
          </h3>
          <p>
            {t(
              row.active
                ? operationalNoticeMessages[row.kind]
                : 'This notice is resolved. Review the current record for its latest status.'
            )}
          </p>
          <p className="text-sm text-slate-600">
            {t('Recorded')}: {new Date(row.createdAt).toLocaleString(locale)}
            {row.resolvedAt && (
              <>
                {' '}
                · {t('Notice resolved')}: {new Date(row.resolvedAt).toLocaleString(locale)}
              </>
            )}
          </p>
          {admin && (
            <p className="text-sm">
              {t('Distributor reference')}: {row.distributorId}
            </p>
          )}
          <p className="text-sm">
            {t(row.kind.startsWith('order_') ? 'Order reference' : 'Purchase reference')}:{' '}
            {row.sourceLabel || row.sourceId}
          </p>
          <Link
            className={admin ? 'underline text-blue-700' : 'me-dw-link underline'}
            to={
              row.kind.startsWith('order_')
                ? admin
                  ? `/admin/distributor-orders?distributorId=${row.distributorId.toLowerCase()}`
                  : '/distributor/orders'
                : admin
                  ? '/admin/distributor-bank'
                  : '/distributor/bank'
            }
          >
            {t(row.kind.startsWith('order_') ? 'Review orders' : 'Review purchases')}
          </Link>
        </article>
      ))}
      {queue?.totalPages > 1 && (
        <nav aria-label={t('Operational notice pages')} className="flex gap-3">
          <button type="button" disabled={loading || page <= 1} onClick={() => load(page - 1)}>
            {t('Previous')}
          </button>
          <span>{t('Page {page} of {pages}', { page, pages: queue.totalPages })}</span>
          <button
            type="button"
            disabled={loading || page >= queue.totalPages}
            onClick={() => load(page + 1)}
          >
            {t('Next')}
          </button>
        </nav>
      )}
    </section>
  );
}
